// Ported as-is from Waffy-web-next (src/components/contract/statusTimeline/StatusStepper.tsx), the
// stepper of C2C and milestone contracts, so a portal two-party transaction shows the same
// stages (WAF-3235). Differences: the contract is a prop instead of web-next's ContractContext,
// and web-next's `isMilestone` query param is the `isMilestone` prop. The portal's share link
// does not carry that param, so a buyer of a portal transaction sees "إنهاء المعاملة" as the last
// step; the prop defaults to the same.
import { useCallback } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import styles from "./statusStepper.module.scss";
import { ECONTRACT_STATUS } from "../../../enums";
import {
  EContractType,
  IContractDetailsResponseData,
} from "../../../setup/api/services/contract/models";

interface IStatusStepperProps {
  contract?: IContractDetailsResponseData;
  /** web-next's `?isMilestone=true`: the last step reads "تحويل الأموال". */
  isMilestone?: boolean;
}

const StatusStepper = ({
  contract,
  isMilestone = false,
}: IStatusStepperProps) => {
  const { t } = useTranslation();

  const Step1 = useCallback(() => {
    const currentStep =
      contract?.status === ECONTRACT_STATUS.CREATED ||
      contract?.status === ECONTRACT_STATUS.SUBMITTED ||
      contract?.status === ECONTRACT_STATUS.PARTIALLY_PAID ||
      contract?.status === ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION;

    const completedStep =
      contract?.status !== ECONTRACT_STATUS.CREATED &&
      contract?.status !== ECONTRACT_STATUS.PARTIALLY_PAID &&
      contract?.status !== ECONTRACT_STATUS.SUBMITTED &&
      contract?.status !== ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION;
    let bgStyle = `${styles[""]}`;
    let textStyle = `${styles[""]}`;
    let statusText = `${styles[""]}`;
    if (currentStep) {
      bgStyle = `${styles["selectedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["selectedStatus"]}`;
    }
    if (completedStep) {
      bgStyle = `${styles["completedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["completedStatusText"]}`;
    }

    return (
      <div className={styles["step"]}>
        <div className={`${styles["numbering"]} ${bgStyle}`}>
          <p className={`${textStyle}`}>1</p>
        </div>
        <p className={`${styles["status"]} ${statusText}`}>
          {t("lbl_contract_details_stepper_payment")}
        </p>
      </div>
    );
  }, [contract?.status, t]);

  const Step2 = useCallback(() => {
    const currentStep =
      contract?.status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY ||
      contract?.status === ECONTRACT_STATUS.PARTIAL_CONFIRMED_DELIVERY ||
      contract?.status === ECONTRACT_STATUS.SHIPPED;
    const escalatedStep = contract?.escalations?.some((escalation) => {
      return (
        escalation.escalationType === "CUSTOMER_DENY_DELIVERY" ||
        escalation.escalationType === "PROVIDER_ESCALATE_DELIVERY"
      );
    });

    const resolvedStep =
      escalatedStep &&
      contract?.status !== ECONTRACT_STATUS.DELIVERY_ESCALATION;

    const incompleteStatuses = [
      ECONTRACT_STATUS.CREATED,
      ECONTRACT_STATUS.PARTIALLY_PAID,
      ECONTRACT_STATUS.SUBMITTED,
      ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION,
      ECONTRACT_STATUS.WAITING_FOR_DELIVERY,
      ECONTRACT_STATUS.PARTIAL_CONFIRMED_DELIVERY,
      ECONTRACT_STATUS.DELIVERY_ESCALATION,
      ECONTRACT_STATUS.SHIPPED,
      ECONTRACT_STATUS.PENDING_PAID,
    ];

    const completedStep =
      contract?.status !== undefined &&
      !incompleteStatuses.includes(contract.status);

    let bgStyle = `${styles[""]}`;
    let textStyle = `${styles[""]}`;
    let statusText = `${styles[""]}`;
    if (currentStep) {
      bgStyle = `${styles["selectedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["selectedStatus"]}`;
    }
    if (completedStep) {
      bgStyle = `${styles["completedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["completedStatusText"]}`;
    }
    if (escalatedStep || resolvedStep) {
      bgStyle = `${styles["escalatedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["selectedStatus"]}`;
    }
    return (
      <div className={styles["step"]}>
        <div className={`${styles["numbering"]} ${bgStyle}`}>
          <p className={`${textStyle}`}>2</p>
        </div>
        <p className={`${styles["status"]} ${statusText}`}>
          {t("lbl_contract_details_stepper_execution")}
        </p>
        {escalatedStep && !resolvedStep && (
          <p className={styles["step_label"]}>
            {t("lbl_delivery_inspection_escalation_contract_status_title")}
          </p>
        )}
        {resolvedStep && (
          <p className={styles["step_label"]}>
            {t("lbl_contract_details_stepper_arbiterated")}
          </p>
        )}
      </div>
    );
  }, [contract?.status, t, contract?.escalations]);

  const Step3 = useCallback(() => {
    const currentStep =
      contract?.status === ECONTRACT_STATUS.ITEM_INSPECTION ||
      contract?.status === ECONTRACT_STATUS.PARTIAL_ITEM_ACCEPTED;

    const escalatedStep = contract?.escalations?.some((escalation) => {
      return (
        escalation.escalationType === "CUSTOMER_REJECT_ITEM" ||
        escalation.escalationType === "PROVIDER_ESCALATE_ITEM"
      );
    });

    const resolvedStep =
      escalatedStep &&
      contract?.status !== ECONTRACT_STATUS.INSPECTION_ESCALATION;

    const adminConfirmedDeliveryFromEscalateDelivery =
      contract?.escalations?.some((escalation) => {
        return (
          (escalation.escalationType === "CUSTOMER_DENY_DELIVERY" ||
            escalation.escalationType === "PROVIDER_ESCALATE_DELIVERY") &&
          escalation.action === "ADMIN_CONFIRM_DELIVERY"
        );
      });

    const skippedStep = adminConfirmedDeliveryFromEscalateDelivery
      ? false
      : contract?.escalations?.some((escalation) => {
          return (
            escalation.escalationType === "CUSTOMER_DENY_DELIVERY" ||
            escalation.escalationType === "PROVIDER_ESCALATE_DELIVERY"
          );
        }) &&
        contract?.status !== ECONTRACT_STATUS.DELIVERY_ESCALATION &&
        contract?.status !== ECONTRACT_STATUS.ITEM_INSPECTION;

    const completedStep =
      contract?.status !== ECONTRACT_STATUS.CREATED &&
      contract?.status !== ECONTRACT_STATUS.PARTIALLY_PAID &&
      contract?.status !== ECONTRACT_STATUS.SUBMITTED &&
      contract?.status !== ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION &&
      contract?.status !== ECONTRACT_STATUS.WAITING_FOR_DELIVERY &&
      contract?.status !== ECONTRACT_STATUS.PARTIAL_CONFIRMED_DELIVERY &&
      contract?.status !== ECONTRACT_STATUS.ITEM_INSPECTION &&
      contract?.status !== ECONTRACT_STATUS.PARTIAL_ITEM_ACCEPTED &&
      contract?.status !== ECONTRACT_STATUS.DELIVERY_ESCALATION &&
      contract?.status !== ECONTRACT_STATUS.INSPECTION_ESCALATION &&
      contract?.status !== ECONTRACT_STATUS.SHIPPED &&
      contract?.status !== ECONTRACT_STATUS.PENDING_PAID;

    let bgStyle = `${styles[""]}`;
    let textStyle = `${styles[""]}`;
    let statusText = `${styles[""]}`;
    if (currentStep) {
      bgStyle = `${styles["selectedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["selectedStatus"]}`;
    }
    if (completedStep) {
      bgStyle = `${styles["completedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["completedStatusText"]}`;
    }
    if (escalatedStep || resolvedStep || skippedStep) {
      bgStyle = `${styles["escalatedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["selectedStatus"]}`;
    }
    return (
      <div className={styles["step"]}>
        <div className={`${styles["numbering"]} ${bgStyle}`}>
          <p className={`${textStyle}`}>
            {skippedStep
              ? `${t("lbl_contract_details_stepper_skip")}`
              : contract?.isDeliverable === false
              ? `2`
              : `3`}
          </p>
        </div>
        <p
          className={classNames(
            `${styles["status"]} ${statusText}`,
            skippedStep ? styles.line_through : ""
          )}>
          {t("lbl_contract_details_stepper_inspection")}
          {escalatedStep && !resolvedStep && !skippedStep && (
            <p className={styles["step_label"]}>
              {t("lbl_delivery_inspection_escalation_contract_status_title")}
            </p>
          )}
          {resolvedStep && !skippedStep && (
            <p className={styles["step_label"]}>
              {t("lbl_contract_details_stepper_arbiterated")}
            </p>
          )}
        </p>
      </div>
    );
  }, [contract?.status, contract?.escalations, contract?.isDeliverable, t]);

  const Step4 = useCallback(() => {
    const currentStep =
      contract?.status === ECONTRACT_STATUS.ITEM_ACCEPTED ||
      contract?.status === ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER ||
      contract?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER ||
      contract?.status === ECONTRACT_STATUS.READY_FOR_CASH_OUT ||
      contract?.status === ECONTRACT_STATUS.CASH_OUT_APPROVED;
    const completedStep =
      contract?.status !== ECONTRACT_STATUS.CREATED &&
      contract?.status !== ECONTRACT_STATUS.PARTIALLY_PAID &&
      contract?.status !== ECONTRACT_STATUS.SUBMITTED &&
      contract?.status !== ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION &&
      contract?.status !== ECONTRACT_STATUS.WAITING_FOR_DELIVERY &&
      contract?.status !== ECONTRACT_STATUS.PARTIAL_CONFIRMED_DELIVERY &&
      contract?.status !== ECONTRACT_STATUS.ITEM_INSPECTION &&
      contract?.status !== ECONTRACT_STATUS.PARTIAL_ITEM_ACCEPTED &&
      contract?.status !== ECONTRACT_STATUS.ITEM_ACCEPTED &&
      contract?.status !== ECONTRACT_STATUS.DELIVERY_ESCALATION &&
      contract?.status !== ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER &&
      contract?.status !== ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER &&
      contract?.status !== ECONTRACT_STATUS.INSPECTION_ESCALATION &&
      contract?.status !== ECONTRACT_STATUS.READY_FOR_CASH_OUT &&
      contract?.status !== ECONTRACT_STATUS.CASH_OUT_APPROVED &&
      contract?.status !== ECONTRACT_STATUS.SHIPPED &&
      contract?.status !== ECONTRACT_STATUS.PENDING_PAID;

    let bgStyle = `${styles[""]}`;
    let textStyle = `${styles[""]}`;
    let statusText = `${styles[""]}`;
    if (currentStep) {
      bgStyle = `${styles["selectedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["selectedStatus"]}`;
    }
    if (completedStep) {
      bgStyle = `${styles["completedBg"]}`;
      textStyle = `${styles["selectedText"]}`;
      statusText = `${styles["completedStatusText"]}`;
    }
    // Calculate step number based on which steps are shown
    const stepNumber = () => {
      let number = 4;
      if (contract?.isDeliverable === false) number--;
      if (
        contract?.type === EContractType.MILESTONE_CONTRACT &&
        !contract?.isInspectable
      )
        number--;
      return number.toString();
    };

    return (
      <div className={classNames(styles["step"], styles["last_step"])}>
        <div className={`${styles["numbering"]} ${bgStyle}`}>
          <p className={`${textStyle}`}>{stepNumber()}</p>
        </div>
        <p className={`${styles["status"]} ${statusText}`}>
          {isMilestone || contract?.organizationCode === "Haraj"
            ? t("lbl_haraj_contract_details_stepper_ending")
            : t("lbl_contract_details_stepper_ending")}
        </p>
      </div>
    );
  }, [
    contract?.status,
    contract?.isDeliverable,
    contract?.isInspectable,
    contract?.organizationCode,
    isMilestone,
    t,
    contract?.type,
  ]);

  return (
    <div className={styles["container"]}>
      <div className={styles["time_line"]}>
        <Step1 />
        <div className={styles["line"]} />
        {contract?.isDeliverable !== false && (
          <>
            <Step2 />
            <div className={styles["line"]} />
          </>
        )}
        {((contract?.type === EContractType.MILESTONE_CONTRACT &&
          contract?.isInspectable) ||
          contract?.type !== EContractType.MILESTONE_CONTRACT) && (
          <>
            <Step3 />
            <div className={styles["line"]} />
          </>
        )}
        <Step4 />
      </div>
    </div>
  );
};

export default StatusStepper;
